import { useState, useEffect } from "react";
import { Info, Eye, Pause, Play, Square, Loader2, Cloud, HardDrive } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/clientWeb/components/ui/tooltip";
import { NetworkQualityIndicator } from "@vc/views/vcLayout/components/overlays/NetworkQualityIndicator";
import { formatDuration } from "../hooks/utils";

interface LeftSectionProps {
  meetingStartTime?: Date;
  meetingCode: string;
  /** Real meeting/webinar title from the persisted record. Falls back to a
   *  generic i18n label only when absent (legacy callers). */
  meetingName?: string;
  networkQuality: {
    quality: "excellent" | "good" | "fair" | "poor" | "offline" | "unknown";
    /** Null until measured. Rendered as "—", never as a stand-in number. */
    bitrate: number | null;
    latency: number | null;
    packetLoss: number | null;
  };
  onOpenMeetingInfo?: () => void;
  compact?: boolean;
  /** This user's own cloud recording is running. */
  cloudRecordingOn?: boolean;
  cloudRecordingDuration?: string;
  onStopCloudRecording?: () => void;
  /** A cloud stop is in flight: the chip shows "Stopping..." and the Stop
   *  control spins and stops responding until the mixer confirms. */
  cloudRecordingStopping?: boolean;
  /** This user's own local (browser) recording is running. */
  localRecordingOn?: boolean;
  localRecordingDuration?: string;
  localRecordingPaused?: boolean;
  onPauseLocalRecording?: () => void;
  onResumeLocalRecording?: () => void;
  onStopLocalRecording?: () => void;
  /** Someone else in the meeting is recording. Indicator only: no controls,
   *  since this viewer has no say over a recording that is not theirs. */
  othersRecording?: boolean;
  viewerCount?: number;
}

export function LeftSection({
  meetingStartTime,
  meetingName,
  networkQuality,
  onOpenMeetingInfo,
  compact = false,
  cloudRecordingOn = false,
  cloudRecordingDuration,
  onStopCloudRecording,
  cloudRecordingStopping = false,
  localRecordingOn = false,
  localRecordingDuration,
  localRecordingPaused = false,
  onPauseLocalRecording,
  onResumeLocalRecording,
  onStopLocalRecording,
  othersRecording = false,
  viewerCount = 0,
}: LeftSectionProps) {

  // Local recording is the only one with pause anywhere in the stack; the
  // cloud recording's mixer only accepts set_file_output and stop.
  const canPauseLocalRecording = Boolean(localRecordingPaused ? onResumeLocalRecording : onPauseLocalRecording);
  const t = useTranslation();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const durationString = formatDuration(meetingStartTime, now);

  // Compact mode: Only show info icon + recording + viewers
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {/* Cloud recording - Compact */}
        {cloudRecordingOn && (
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10"
            aria-label={t("controlBar.cloudRecording")}
          >
            <Cloud className="h-2.5 w-2.5 shrink-0 text-red-400" />
            {cloudRecordingStopping ? (
              <Loader2 className="h-2.5 w-2.5 shrink-0 animate-spin text-red-400" />
            ) : (
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-red-400"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
            )}
            <span className="text-xs font-medium tabular-nums text-red-400">
              {cloudRecordingStopping
                ? t("recordingStatus.stopping")
                : `REC${cloudRecordingDuration ? ` ${cloudRecordingDuration}` : ""}`}
            </span>
          </div>
        )}

        {/* Local recording - Compact */}
        {localRecordingOn && (
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10"
            aria-label={t("controlBar.localRecording")}
          >
            <HardDrive className={cn("h-2.5 w-2.5 shrink-0", localRecordingPaused ? "text-amber-400" : "text-red-400")} />
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className={cn(
                "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                localRecordingPaused ? "bg-amber-400" : "bg-red-400"
              )}></span>
              <span className={cn(
                "relative inline-flex rounded-full h-2.5 w-2.5",
                localRecordingPaused ? "bg-amber-500" : "bg-red-500"
              )}></span>
            </span>
            <span className={cn(
              "text-xs font-medium tabular-nums",
              localRecordingPaused ? "text-amber-400" : "text-red-400"
            )}>
              {localRecordingPaused ? "PAUSED" : `REC${localRecordingDuration ? ` ${localRecordingDuration}` : ""}`}
            </span>
          </div>
        )}

        {/* Someone else's recording: the same REC look as an own recording,
            but no timer and no controls, since it is not this viewer's. */}
        {othersRecording && (
          <div
            role="status"
            aria-label={t("recordingStatus.othersRecordingTooltip")}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/10"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-red-400"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>
            <span className="text-xs font-medium text-red-400">REC</span>
          </div>
        )}

        {/* Viewer Count */}
        {viewerCount > 0 && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-card/10 text-white/80">
                  <Eye size={14} />
                  <span className="text-xs font-medium">{viewerCount}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                {viewerCount} {viewerCount > 1 ? t("controlBar.viewers") : t("controlBar.viewer")} {t("controlBar.watching")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onOpenMeetingInfo}
                disabled={!onOpenMeetingInfo}
                className={cn(
                  "rounded-md p-2 flex items-center justify-center transition cursor-pointer",
                  onOpenMeetingInfo
                    ? "hover:bg-card/10 text-white/80"
                    : "cursor-default opacity-80",
                )}
                aria-label={t("controlBar.meetingDetails")}
              >
                <Info size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={8} className="border-none capitalize">
              {t("controlBar.meetingDetails")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Full mode: Show time, network quality, duration, meeting code, and recording controls
  return (
    <div className="flex items-center gap-3 text-md font-bold text-white/70">
      <div className="w-7 h-7">
        <img src="/in.svg" className="w-full h-full" alt="" />
      </div>
      {meetingStartTime && (
        <>
          <span className="text-white/70">{durationString}</span>
          <span className="text-white/40">|</span>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center">
                  <NetworkQualityIndicator
                    quality={networkQuality.quality}
                    bitrate={networkQuality.bitrate}
                    packetLoss={networkQuality.packetLoss}
                    latency={networkQuality.latency}
                    size="sm"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                <div className="text-xs">
                  <div className="font-medium capitalize">
                    {networkQuality.quality === "unknown" ? "Measuring connection" : `${networkQuality.quality} Connection`}
                  </div>
                  <div className="text-white/70">
                    {/* An em dash where there is no measurement. The previous
                        implementation filled these with invented numbers. */}
                    {networkQuality.bitrate ?? "—"} kbps • {networkQuality.latency ?? "—"}ms •{" "}
                    {networkQuality.packetLoss === null ? "—" : networkQuality.packetLoss.toFixed(1)}% loss
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}

      {/* Cloud recording chip: no pause anywhere in the stack (the mixer's
          compositor accepts set_file_output and stop, nothing else), so only
          a Stop control. */}
      {cloudRecordingOn && (
        <div className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border transition-colors bg-red-500/10 border-red-500/30">
          <Cloud className="h-3 w-3 shrink-0 text-red-400" aria-hidden />
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping bg-red-400"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
          </span>

          <span className="text-xs font-semibold tabular-nums text-red-400">
            {cloudRecordingStopping
              ? t("recordingStatus.stopping")
              : <>REC{cloudRecordingDuration ? ` ${cloudRecordingDuration}` : ""}</>}
          </span>

          {onStopCloudRecording && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={cloudRecordingStopping ? undefined : onStopCloudRecording}
                  disabled={cloudRecordingStopping}
                  aria-busy={cloudRecordingStopping}
                  aria-label={cloudRecordingStopping ? t("recordingStatus.stopping") : `${t("controlBar.cloudRecording")} ${t("recordingStatus.stop")}`}
                  className={cn(
                    "p-1 rounded-full text-red-400 transition-colors",
                    cloudRecordingStopping ? "opacity-70 cursor-not-allowed" : "hover:bg-red-500/20 cursor-pointer"
                  )}
                >
                  {cloudRecordingStopping
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Square className="h-3 w-3 fill-current" />
                  }
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                {cloudRecordingStopping ? t("recordingStatus.stopping") : t("recordingStatus.stop")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      )}

      {/* Local recording chip: pause/resume plus stop, independent of the
          cloud chip above so stopping one never touches the other. */}
      {localRecordingOn && (
        <div className={cn(
          "flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border transition-colors",
          localRecordingPaused
            ? "bg-amber-500/10 border-amber-500/30"
            : "bg-red-500/10 border-red-500/30"
        )}>
          <HardDrive className={cn("h-3 w-3 shrink-0", localRecordingPaused ? "text-amber-400" : "text-red-400")} aria-hidden />
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className={cn(
              "absolute inline-flex h-full w-full rounded-full opacity-75",
              localRecordingPaused ? "bg-amber-400" : "animate-ping bg-red-400"
            )}></span>
            <span className={cn(
              "relative inline-flex rounded-full h-2.5 w-2.5",
              localRecordingPaused ? "bg-amber-500" : "bg-red-500"
            )}></span>
          </span>

          <span className={cn(
            "text-xs font-semibold tabular-nums",
            localRecordingPaused ? "text-amber-400" : "text-red-400"
          )}>
            {localRecordingPaused ? "PAUSED" : <>REC{localRecordingDuration ? ` ${localRecordingDuration}` : ""}</>}
          </span>

          {canPauseLocalRecording && (
          <>
          <span className={cn("w-px h-3.5 mx-0.5", localRecordingPaused ? "bg-amber-500/30" : "bg-red-500/30")} />

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={localRecordingPaused ? onResumeLocalRecording : onPauseLocalRecording}
                  className={cn(
                    "p-1 rounded-full transition-colors cursor-pointer",
                    localRecordingPaused
                      ? "text-amber-400 hover:bg-amber-500/20"
                      : "text-red-400 hover:bg-red-500/20"
                  )}
                  aria-label={localRecordingPaused ? t("recordingStatus.resume") : t("recordingStatus.pause")}
                >
                  {localRecordingPaused
                    ? <Play className="h-3 w-3 fill-current" />
                    : <Pause className="h-3 w-3 fill-current" />
                  }
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                {localRecordingPaused ? t("recordingStatus.resume") : t("recordingStatus.pause")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          </>
          )}

          {onStopLocalRecording && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onStopLocalRecording}
                  aria-label={`${t("controlBar.localRecording")} ${t("recordingStatus.stop")}`}
                  className="p-1 rounded-full text-red-400 transition-colors hover:bg-red-500/20 cursor-pointer"
                >
                  <Square className="h-3 w-3 fill-current" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                {t("recordingStatus.stop")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      )}

      {/* Someone else's recording: the same REC chip as an own recording, but
          no timer and no controls. The tooltip says whose it is. */}
      {othersRecording && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                role="status"
                aria-label={t("recordingStatus.othersRecordingTooltip")}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-red-500/10 border-red-500/30"
              >
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping bg-red-400"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                <span className="text-xs font-semibold text-red-400">REC</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
              {t("recordingStatus.othersRecordingTooltip")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Viewer Count - Full Mode */}
      {viewerCount > 0 && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-card/10 text-white/80">
                <Eye size={14} />
                <span className="text-xs font-medium">{viewerCount}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={8} className="border-none capitalize">
              {viewerCount} {viewerCount > 1 ? t("controlBar.viewers") : t("controlBar.viewer")} {t("controlBar.watching")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onOpenMeetingInfo}
              disabled={!onOpenMeetingInfo}
              className={cn(
                "lowercase rounded-md text-sm px-2 py-1 flex items-center gap-2 transition cursor-pointer max-w-[200px] md:max-w-[250px]",
                onOpenMeetingInfo
                  ? "hover:bg-card/10 hover:text-white"
                  : "cursor-default opacity-80",
              )}
              aria-label={t("controlBar.meetingDetails")}
            >
              <Info size={16} />
              <span className="truncate">
                {meetingName?.trim() || t("controlBar.conferencingRoom")}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={8} className="border-none capitalize">
            {meetingName?.trim() || t("controlBar.conferencingRoom")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
